'use client';

/* eslint-disable @next/next/no-img-element -- The source header uses intrinsic SVG sizing from the original design. */

import { UserRound, X } from 'lucide-react';
import { SessionProvider, useSession } from 'next-auth/react';
import { ThemeProvider } from 'next-themes';
import { PiStarBold } from 'react-icons/pi';
import { LandingLink as Link } from './landing-link';
import { LandingSourceButton, LandingThemeToggle } from './landing-controls';
import { landingNavigation } from '@/app/shared/landing/layout/landing-navigation';

function MenuGridIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4 5C4 4.45 4.45 4 5 4h4c.55 0 1 .45 1 1v4c0 .55-.45 1-1 1H5c-.55 0-1-.45-1-1V5Zm10 0c0-.55.45-1 1-1h4c.55 0 1 .45 1 1v4c0 .55-.45 1-1 1h-4c-.55 0-1-.45-1-1V5ZM4 15c0-.55.45-1 1-1h4c.55 0 1 .45 1 1v4c0 .55-.45 1-1 1H5c-.55 0-1-.45-1-1v-4Zm10 0c0-.55.45-1 1-1h4c.55 0 1 .45 1 1v4c0 .55-.45 1-1 1h-4c-.55 0-1-.45-1-1v-4Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function QuoteLink({ mobile = false }: { mobile?: boolean }) {
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';

  return (
    <div
      className={`sec-1-home-12__btn-primary ${mobile ? '' : 'desktop-quote-cta'}`}
    >
      <Link className="sec-1-home-12__btn-primary-label" href="/get-quote">
        Freight Quote
      </Link>
      <Link
        className="sec-1-home-12__btn-primary-icon"
        href={isAuthenticated ? '/dashboard' : '/auth/sign-in'}
        aria-label={isAuthenticated ? 'Open dashboard' : 'Sign in'}
      >
        {isAuthenticated ? (
          <UserRound aria-hidden="true" width={20} height={20} />
        ) : (
          <svg
            aria-hidden="true"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7 17 17 7m0 0H8m9 0v9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </Link>
    </div>
  );
}

function LandingMobileDrawer() {
  return (
    <>
      <div className="body-overlay" />
      <div className="at-offcanvas-2-area landing-mobile-drawer">
        <div className="offcanvas-bg" />
        <div className="at-offcanvas-2-wrapper offcanvas-menu">
          <div className="at-offcanvas-2-left">
            <div className="at-header-logo d-flex justify-content-between align-items-center mb-50">
              <Link href="/">
                <img
                  data-width="190"
                  src="/landing/assets/images/shipsmart/logo.svg"
                  alt="Ship Smart Solutions"
                  loading="lazy"
                  decoding="async"
                />
              </Link>
              <LandingSourceButton
                type="button"
                variant="text"
                className="hamburger-close-btn"
                aria-label="Close menu"
              >
                <X
                  aria-hidden="true"
                  width={37}
                  height={38}
                  strokeWidth={1.5}
                />
              </LandingSourceButton>
            </div>

            <div className="at-offcanvas-menu counter-row">
              <nav aria-label="Mobile navigation">
                <ul>
                  {landingNavigation.map((item) => (
                    <li
                      key={item.label}
                      className={
                        item.children.length ? 'has-dropdown' : undefined
                      }
                    >
                      {item.children.length ? (
                        <LandingSourceButton
                          type="button"
                          variant="text"
                          className="at-mobile-menu-parent-trigger"
                          aria-expanded="false"
                        >
                          <span>{item.label}</span>
                          <span className="at-menu-close" aria-hidden="true">
                            <i>
                              <svg
                                width="28"
                                height="28"
                                viewBox="0 0 28 28"
                                fill="none"
                              >
                                <path
                                  d="M14 3v22M3 14h22"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </i>
                          </span>
                        </LandingSourceButton>
                      ) : (
                        <Link href={item.href ?? '/'}>{item.label}</Link>
                      )}
                      {item.children.length ? (
                        <ul
                          id={`mobile-menu-${item.label.toLowerCase().replaceAll(' ', '-')}`}
                          className="at-submenu submenu"
                        >
                          {item.children.map((child) => (
                            <li key={child.href}>
                              <Link href={child.href}>{child.label}</Link>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            <div className="mobile-menu-quote">
              <QuoteLink mobile />
            </div>

            <div className="at-offcanvas-social">
              <ul className="at-offcanvas-social__grid">
                <li>
                  <a
                    href="https://clutch.co/profile/ship-smart-solutions"
                    className="at-offcanvas-social__link"
                    aria-label="Clutch"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <PiStarBold aria-hidden="true" width={18} height={18} />
                    <span>Clutch</span>
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.facebook.com/people/Ship-Smart-Solutions-INC/61580481933361/"
                    className="at-offcanvas-social__link"
                    aria-label="Facebook"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M12.4024 18V11.0344H14.7347L15.0838 8.3265H12.4024V6.59765C12.4024 5.81364 12.62 5.27934 13.7443 5.27934L15.1783 5.27867V2.85676C14.9302 2.82382 14.0791 2.75006 13.0888 2.75006C11.0213 2.75006 9.606 4.01198 9.606 6.32952V8.3265H7.2677V11.0344H9.606V18H1C0.44772 18 0 17.5523 0 17V1C0 0.44772 0.44772 0 1 0H17C17.5523 0 18 0.44772 18 1V17C18 17.5523 17.5523 18 17 18H12.4024Z"
                        fill="currentColor"
                      />
                    </svg>
                    <span>Facebook</span>
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.linkedin.com/company/shipsmartsolutions/people/"
                    className="at-offcanvas-social__link"
                    aria-label="LinkedIn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M15.3352 15.339H12.6697V11.1622C12.6697 10.1662 12.6495 8.8845 11.2807 8.8845C9.89102 8.8845 9.67872 9.9683 9.67872 11.0887V15.339H7.01322V6.75H9.57372V7.9207H9.60822C9.96602 7.2457 10.836 6.53325 12.1357 6.53325C14.8365 6.53325 15.336 8.3108 15.336 10.6245L15.3352 15.339ZM4.00275 5.57475C3.14475 5.57475 2.4555 4.88025 2.4555 4.026C2.4555 3.1725 3.1455 2.47875 4.00275 2.47875C4.85775 2.47875 5.55075 3.1725 5.55075 4.026C5.55075 4.88025 4.857 5.57475 4.00275 5.57475ZM5.33925 15.339H2.66625V6.75H5.33925V15.339ZM16.6687 0H1.32825C0.594 0 0 0.5805 0 1.29675V16.7033C0 17.4202 0.594 18 1.32825 18H16.6665C17.4 18 18 17.4202 18 16.7033V1.29675C18 0.5805 17.4 0 16.6665 0H16.6687Z"
                        fill="currentColor"
                      />
                    </svg>
                    <span>Linkedin</span>
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.instagram.com/ship.smart.solutions/"
                    className="at-offcanvas-social__link"
                    aria-label="Instagram"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M9.92475 0C10.9376 0.00167406 11.4509 0.00703828 11.8945 0.0202418L12.0692 0.025948C12.2709 0.0331213 12.47 0.0421215 12.7101 0.0533719C13.6678 0.0976265 14.3211 0.249129 14.8949 0.471887C15.4881 0.700648 15.9892 1.00966 16.4894 1.50993C16.9889 2.0102 17.298 2.51271 17.5275 3.10448C17.7494 3.67751 17.901 4.33153 17.946 5.28931C17.9567 5.52933 17.9653 5.72841 17.9725 5.93024L17.9781 6.10492C17.9913 6.5484 17.9973 7.06177 17.9992 8.07471L17.9999 8.74577C18 8.82777 18 8.91237 18 8.99967L17.9999 9.25357L17.9994 9.92472C17.9977 10.9376 17.9923 11.451 17.9791 11.8944L17.9734 12.0691C17.9662 12.271 17.9572 12.4701 17.946 12.7101C17.9017 13.6679 17.7494 14.3211 17.5275 14.8949C17.2987 15.4882 16.9889 15.9892 16.4894 16.4895C15.9892 16.989 15.4859 17.298 14.8949 17.5275C14.3211 17.7495 13.6678 17.901 12.7101 17.946C12.47 17.9567 12.2709 17.9654 12.0692 17.9725L11.8945 17.9781C11.4509 17.9914 10.9376 17.9973 9.92475 17.9993L9.25359 18C9.1716 18 9.087 18 8.99969 18H8.7458L8.07464 17.9994C7.06175 17.9977 6.54839 17.9924 6.1049 17.9791L5.93022 17.9734C5.72839 17.9662 5.5293 17.9572 5.2893 17.946C4.33151 17.9018 3.67899 17.7495 3.10448 17.5275C2.51195 17.2988 2.01018 16.989 1.50992 16.4895C1.00965 15.9892 0.701394 15.4859 0.471886 14.8949C0.249129 14.3211 0.0983735 13.6679 0.053372 12.7101C0.0426796 12.4701 0.0339582 12.271 0.026893 12.0691L0.0212318 11.8944C0.00806435 11.451 0.00206123 10.9376 0.000117163 9.92472L0 8.07471C0.00167406 7.06177 0.00702925 6.5484 0.0202327 6.10492L0.0259479 5.93024C0.0331212 5.72841 0.0421216 5.52933 0.053372 5.28931C0.0976175 4.33078 0.249129 3.67825 0.471886 3.10448C0.700638 2.51196 1.00965 2.0102 1.50992 1.50993C2.01018 1.00966 2.51271 0.701396 3.10448 0.471887C3.67825 0.249129 4.33077 0.0983735 5.2893 0.0533719C5.5293 0.0426885 5.72839 0.0339673 5.93022 0.026902L6.1049 0.0212407C6.54839 0.00806417 7.06175 0.00206102 8.07464 0.000116949L9.92475 0ZM8.99969 4.49953C6.51298 4.49953 4.49952 6.51518 4.49952 8.99967C4.49952 11.4864 6.51517 13.4998 8.99969 13.4998C11.4864 13.4998 13.4998 11.4842 13.4998 8.99967C13.4998 6.513 11.4841 4.49953 8.99969 4.49953ZM8.99969 6.2996C10.491 6.2996 11.6998 7.50805 11.6998 8.99967C11.6998 10.4909 10.4913 11.6998 8.99969 11.6998C7.50843 11.6998 6.29958 10.4914 6.29958 8.99967C6.29958 7.50841 7.50798 6.2996 8.99969 6.2996ZM13.7249 3.14949C13.1045 3.14949 12.5998 3.65342 12.5998 4.27376C12.5998 4.89411 13.1037 5.39882 13.7249 5.39882C14.3452 5.39882 14.8499 4.89489 14.8499 4.27376C14.8499 3.65342 14.3443 3.14871 13.7249 3.14949Z"
                        fill="currentColor"
                      />
                    </svg>
                    <span>Instagram</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function LandingSourceHeaderContent() {
  return (
    <>
      <header className="header-8 header-12">
        <div className="at-header-area at-header-spacing header-transparent mt-2">
          <div className="ship-smart-content-rail container">
            <div className="row align-items-center relative">
              <div className="col-xxl-3 col-lg-1 col-6 min-[1000px]:!w-[220px] min-[1000px]:!min-w-[220px] min-[1000px]:!max-w-[220px] min-[1000px]:!flex-none">
                <Link
                  className="at-header-logo min-[1000px]:!w-[220px]"
                  href="/"
                >
                  <img
                    data-width="220"
                    src="/landing/assets/images/shipsmart/logo.svg"
                    alt="Ship Smart Solutions"
                    fetchPriority="high"
                    decoding="async"
                    className="min-[1000px]:!h-auto min-[1000px]:!w-[220px] min-[1000px]:!max-w-none"
                  />
                </Link>
              </div>

              <div className="landing-desktop-navigation col-xl-6 d-none d-xl-block align-items-center me-auto text-center min-[1200px]:!absolute min-[1200px]:!left-1/2 min-[1200px]:!m-0 min-[1200px]:!w-auto min-[1200px]:!-translate-x-1/2">
                <div className="at-main-menu d-inline-flex justify-content-center">
                  <nav
                    className="at-mobile-menu-active"
                    aria-label="Main navigation"
                  >
                    <ul>
                      {landingNavigation.map((item) => (
                        <li
                          key={item.label}
                          className={
                            item.children.length ? 'has-dropdown' : undefined
                          }
                        >
                          {item.href ? (
                            <Link href={item.href}>{item.label}</Link>
                          ) : (
                            // No index page for this section, the label only opens
                            // the dropdown, the children are the real links.
                            <a>{item.label}</a>
                          )}
                          {item.children.length ? (
                            <ul className="at-submenu submenu">
                              {item.children.map((child) => (
                                <li key={child.href}>
                                  <Link href={child.href}>{child.label}</Link>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </div>

              <div className="col-xl-3 col-6 min-[1000px]:!ml-auto">
                <div className="at-header-right d-flex justify-content-end align-items-center gap-3">
                  <LandingThemeToggle />
                  <QuoteLink />
                  <LandingSourceButton
                    variant="text"
                    className="mobile-menu-trigger hamburger-open-btn"
                    type="button"
                    aria-label="Open mobile menu"
                  >
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M4 7h16M4 12h16M4 17h16"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </LandingSourceButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        <LandingSourceButton
          variant="text"
          id="header-sticky"
          className="hamburger-open-btn hamburger-sticky-menu"
          aria-label="Open navigation menu"
        >
          <span>MENU</span>
          <span className="icon">
            <MenuGridIcon />
          </span>
        </LandingSourceButton>
      </header>
      <LandingMobileDrawer />
    </>
  );
}

export function LandingSourceHeader() {
  return (
    <SessionProvider>
      <ThemeProvider enableSystem={false} defaultTheme="light">
        <LandingSourceHeaderContent />
      </ThemeProvider>
    </SessionProvider>
  );
}
